import { useState, useEffect } from 'react'
import { TrendingUp, BarChart3, Users, Briefcase } from 'lucide-react'
import api from '../core/services/api'

interface StatoInfo { n: number; val: number }
interface PersonInfo { id: string; nome: string; n_offerte?: number; n_commesse?: number }

interface StatsData {
  cliente_id: string
  mesi: Record<string, Record<string, StatoInfo>>
  anni: Record<string, Record<string, StatoInfo>>
  valore_confermato_anno: number
  commerciali: PersonInfo[]
  esecutori: PersonInfo[]
}

const STATO_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  bozza: { label: 'Bozza', color: 'text-gray-600', bg: 'bg-gray-100' },
  in_corso: { label: 'Bozza', color: 'text-gray-600', bg: 'bg-gray-100' },
  in_revisione: { label: 'In revisione', color: 'text-purple-600', bg: 'bg-purple-100' },
  approvata: { label: 'Approvata', color: 'text-blue-600', bg: 'bg-blue-100' },
  in_attesa: { label: 'Inviata', color: 'text-amber-600', bg: 'bg-amber-100' },
  confermata: { label: 'Confermata', color: 'text-green-600', bg: 'bg-green-100' },
  rifiutata: { label: 'Rifiutata', color: 'text-red-600', bg: 'bg-red-100' },
}

const MESE_LABELS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']

function fmtCur(value: number) {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `${Math.round(value / 1000)}k`
  return String(Math.round(value))
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value)
}

export default function ClienteStatsCard({ clienteId }: { clienteId: string }) {
  const [stats, setStats] = useState<StatsData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!clienteId) return
    setLoading(true)
    api.get(`/v1/offerte/statistiche-cliente/${clienteId}`)
      .then(res => setStats(res.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false))
  }, [clienteId])

  if (loading) return <div className="animate-pulse bg-gray-100 rounded-lg h-24" />
  if (!stats) return null

  const mesiKeys = Object.keys(stats.mesi).sort()
  const anniKeys = Object.keys(stats.anni).sort()
  const hasMesi = mesiKeys.length > 0
  const hasAnni = anniKeys.length > 0

  if (!hasMesi && !hasAnni) {
    return (
      <div className="bg-gray-50 rounded-lg p-3 text-center text-sm text-gray-400">
        Nessuna offerta per questo cliente
      </div>
    )
  }

  // Collect all unique states
  const allStati = new Set<string>()
  Object.values(stats.mesi).forEach(m => Object.keys(m).forEach(s => allStati.add(s)))
  Object.values(stats.anni).forEach(a => Object.keys(a).forEach(s => allStati.add(s)))
  const statiOrdinati = ['bozza', 'in_revisione', 'approvata', 'in_attesa', 'confermata', 'rifiutata']
    .filter(s => allStati.has(s) || (s === 'bozza' && allStati.has('in_corso')))

  const getInfo = (data: Record<string, StatoInfo>, stato: string): StatoInfo => {
    if (stato === 'bozza') {
      const b = data['bozza'] || { n: 0, val: 0 }
      const ic = data['in_corso'] || { n: 0, val: 0 }
      return { n: b.n + ic.n, val: b.val + ic.val }
    }
    return data[stato] || { n: 0, val: 0 }
  }

  const getTotals = (data: Record<string, StatoInfo>) => {
    let n = 0, val = 0
    Object.values(data).forEach(info => { n += info.n; val += info.val })
    return { n, val }
  }

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
          <BarChart3 className="w-4 h-4 text-indigo-500" />
          Attivita' Offerte
        </h4>
        {stats.valore_confermato_anno > 0 && (
          <span className="text-sm font-semibold text-green-700 flex items-center gap-1">
            <TrendingUp className="w-3.5 h-3.5" />
            {formatCurrency(stats.valore_confermato_anno)} confermato {new Date().getFullYear()}
          </span>
        )}
      </div>

      {/* Ultimi 3 mesi */}
      {hasMesi && (
        <div>
          <p className="text-xs text-gray-500 mb-1.5 font-medium">Ultimi mesi</p>
          <div className="grid grid-cols-3 gap-2">
            {mesiKeys.map(key => {
              const [anno, mese] = key.split('-')
              const data = stats.mesi[key]
              const totals = getTotals(data)
              return (
                <div key={key} className="bg-white rounded-lg p-2 text-center">
                  <div className="text-xs text-gray-500 mb-1">{MESE_LABELS[parseInt(mese) - 1]} {anno}</div>
                  <div className="text-lg font-bold text-gray-800">{totals.n}</div>
                  {totals.val > 0 && <div className="text-xs text-gray-400">{formatCurrency(totals.val)}</div>}
                  <div className="flex flex-wrap justify-center gap-1 mt-1">
                    {statiOrdinati.map(stato => {
                      const info = getInfo(data, stato)
                      if (info.n === 0) return null
                      const cfg = STATO_LABELS[stato] || { label: stato, color: 'text-gray-500', bg: 'bg-gray-50' }
                      return (
                        <span key={stato} className={`text-xs px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.color}`}>
                          {info.n} {cfg.label.toLowerCase()}{info.val > 0 ? ` \u20AC${fmtCur(info.val)}` : ''}
                        </span>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Confronto annuale */}
      {hasAnni && (
        <div>
          <p className="text-xs text-gray-500 mb-1.5 font-medium">Confronto annuale</p>
          <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${anniKeys.length}, 1fr)` }}>
            {anniKeys.map(anno => {
              const data = stats.anni[anno]
              const totals = getTotals(data)
              return (
                <div key={anno} className="bg-white rounded-lg p-2 text-center">
                  <div className="text-xs text-gray-500 mb-1">{anno}</div>
                  <div className="text-lg font-bold text-gray-800">{totals.n}</div>
                  {totals.val > 0 && <div className="text-xs text-gray-400">{formatCurrency(totals.val)}</div>}
                  <div className="flex flex-wrap justify-center gap-1 mt-1">
                    {statiOrdinati.map(stato => {
                      const info = getInfo(data, stato)
                      if (info.n === 0) return null
                      const cfg = STATO_LABELS[stato] || { label: stato, color: 'text-gray-500', bg: 'bg-gray-50' }
                      return (
                        <span key={stato} className={`text-xs px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.color}`}>
                          {info.n} {cfg.label.toLowerCase()}{info.val > 0 ? ` \u20AC${fmtCur(info.val)}` : ''}
                        </span>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Commerciali + Esecutori */}
      {(stats.commerciali.length > 0 || stats.esecutori.length > 0) && (
        <div className="grid grid-cols-2 gap-3">
          {stats.commerciali.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-1 font-medium flex items-center gap-1">
                <Users className="w-3 h-3" /> Commerciali
              </p>
              <div className="flex flex-wrap gap-1">
                {stats.commerciali.map(c => (
                  <span key={c.id} className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                    {c.nome} <span className="font-bold">{c.n_offerte}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {stats.esecutori.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-1 font-medium flex items-center gap-1">
                <Briefcase className="w-3 h-3" /> Esecutori
              </p>
              <div className="flex flex-wrap gap-1">
                {stats.esecutori.map(e => (
                  <span key={e.id} className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                    {e.nome} <span className="font-bold">{e.n_commesse}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
